import type { Dataset, Property, FinancialPeriod, PropertyInsight } from '@/types/entities'
import { SectionCard, HealthBadge, Badge, EmptyState } from '@/components/ui'
import { FunnelBars } from '@/components/charts'
import { latest } from '@/lib/selectors'
import { num, pct } from '@/lib/format'
import { diagnose, delinquencyRead, netAbsorption } from './propertyMetrics'

export function InsightsTab({ d, p, fp }: { d: Dataset; p: Property; fp?: FinancialPeriod }) {
  // Prefer a seeded property insight if one exists; otherwise compute a diagnostic.
  const seeded: PropertyInsight | undefined = d.propertyInsights.find((i) => i.propertyId === p.id)
  const diag = diagnose(p, fp)
  const occ = latest(p.occupancyPct)
  const del = delinquencyRead(fp)
  const absorption = netAbsorption(p)

  // Operational funnel we can actually build from snapshots:
  //  Occupied SF → (theoretical) full SF, lease velocity, collected (non-delinquent) rent.
  const occSqFt = latest(p.occupiedSqFt)
  const funnelStages = buildFunnel(p, occ, occSqFt, absorption, del.pct)

  const verdict = seeded?.verdict ?? diag.verdict
  const narrative = seeded?.narrative

  return (
    <div className="pd-stack">
      <SectionCard
        eyebrow="WISE Diagnostic"
        title="Asset Verdict"
        right={<Badge tone="info" variant="outline">Diagnostic · auto-computed</Badge>}
      >
        <div className="pd-verdict">
          <HealthBadge health={verdict} />
          <div className="pd-verdict-body">
            <p className="pd-verdict-narr">
              {narrative
                ?? `Composite read across occupancy, delinquency, and revenue trend for ${p.name}. This verdict is generated from current operating snapshots and is intended to direct attention, not replace asset-manager judgment.`}
            </p>
          </div>
        </div>
      </SectionCard>

      <div className="pd-grid pd-grid--2-1">
        <SectionCard eyebrow="Operating Funnel" title="Lease & Collection Funnel">
          {funnelStages.length > 0 ? (
            <>
              <FunnelBars stages={funnelStages} />
              <p className="pd-caption">
                Operational funnel (occupancy → velocity → collections) — marketing-funnel inputs
                are not captured in this dataset, so this substitutes asset-level operating signals.
              </p>
            </>
          ) : (
            <EmptyState title="Insufficient signal" hint="Need occupancy and revenue snapshots to build the funnel." />
          )}
        </SectionCard>

        <SectionCard eyebrow="What's Driving It" title="Diagnostic Drivers">
          <div className="pd-drivers">
            {(seeded?.drivers ?? diag.reasons.map((r) => ({ label: '', detail: r, tone: deriveTone(r) }))).map((dr, i) => {
              const label = 'label' in dr ? dr.label : ''
              const detail = 'detail' in dr ? dr.detail : ''
              const tone = ('tone' in dr ? dr.tone : 'neutral') as 'ok' | 'warn' | 'alert' | 'neutral'
              return (
                <div key={i} className="pd-driver">
                  <span className={`pd-driver-dot pd-driver-dot--${tone}`} />
                  <div>
                    {label && <div className="pd-driver-label">{label}</div>}
                    <div className="pd-driver-detail">{detail}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </SectionCard>
      </div>

      <SectionCard eyebrow="Recommended" title="Recommended Actions">
        {seeded?.recommended && seeded.recommended.length > 0 ? (
          <div className="pd-recs">
            {seeded.recommended.map((r, i) => (
              <div key={i} className="pd-rec">
                <span className="pd-rec-num">{i + 1}</span>
                <div>
                  <div className="pd-rec-title">{r.title}</div>
                  {r.rationale && <div className="pd-rec-rationale">{r.rationale}</div>}
                  <div className="pd-rec-meta">
                    {r.suggestedOwner ? `Owner: ${r.suggestedOwner}` : ''}
                    {r.suggestedOwner && r.priority ? ' · ' : ''}
                    {r.priority ? `${r.priority} priority` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pd-recs">
            {computedRecs(occ, del.pct).map((r, i) => (
              <div key={i} className="pd-rec">
                <span className="pd-rec-num">{i + 1}</span>
                <div>
                  <div className="pd-rec-title">{r.title}</div>
                  <div className="pd-rec-rationale">{r.rationale}</div>
                  <div className="pd-rec-meta">Suggested · Asset Management</div>
                </div>
              </div>
            ))}
            {computedRecs(occ, del.pct).length === 0 && (
              <EmptyState title="No flagged actions" hint="Asset is operating within tolerance on the tracked metrics." />
            )}
          </div>
        )}
      </SectionCard>
    </div>
  )
}

function deriveTone(reason: string): 'ok' | 'warn' | 'alert' | 'neutral' {
  const r = reason.toLowerCase()
  if (r.includes('materially') || r.includes('elevated') || r.includes('down')) return 'alert'
  if (r.includes('below') || r.includes('watch')) return 'warn'
  if (r.includes('above') || r.includes('target') || r.includes('up') || r.includes('in range')) return 'ok'
  return 'neutral'
}

function buildFunnel(
  p: Property,
  occ?: number,
  occSqFt?: number,
  absorption?: number,
  delPct?: number,
): { name: string; value: number; benchmark?: number; display?: string }[] {
  const stages: { name: string; value: number; benchmark?: number; display?: string }[] = []
  if (p.nrsf) {
    stages.push({ name: 'Rentable SF', value: p.nrsf, display: num(p.nrsf) })
  }
  if (occSqFt != null && p.nrsf) {
    stages.push({
      name: 'Occupied SF',
      value: occSqFt,
      benchmark: p.nrsf * 0.9,
      display: occ != null ? `${num(occSqFt)} · ${pct(occ)}` : num(occSqFt),
    })
  }
  if (absorption != null && p.nrsf) {
    // net absorption shown as a small positive bar relative to base
    const abs = Math.max(0, absorption)
    stages.push({ name: 'Net Absorption', value: abs, display: `${absorption >= 0 ? '+' : ''}${num(absorption)} SF` })
  }
  if (delPct != null && occSqFt != null) {
    // delPct is a dollar-based delinquency ratio; applying it to occupied SF yields an
    // illustrative collections proxy, not literal collecting square footage.
    const collectedShare = Math.max(0, occSqFt * (1 - delPct / 100))
    stages.push({ name: 'Est. Collecting (proxy)', value: collectedShare, display: `${pct(100 - delPct)} of in-place` })
  }
  return stages
}

function computedRecs(occ?: number, delPct?: number): { title: string; rationale: string }[] {
  const recs: { title: string; rationale: string }[] = []
  if (delPct != null && delPct >= 8) {
    recs.push({
      title: 'Tighten collections cadence',
      rationale: `Delinquency at ${pct(delPct)} of in-place rent is above tolerance. Escalate lien/auction timeline and review write-off policy with the operator.`,
    })
  }
  if (occ != null && occ < 90) {
    recs.push({
      title: 'Lease-up / pricing review',
      rationale: `Physical occupancy at ${pct(occ)} is below the 90% stabilized target. Coordinate Revenue Management on rate and concession strategy.`,
    })
  }
  if (occ != null && occ < 85) {
    recs.push({
      title: 'Escalate to monthly asset review',
      rationale: `Occupancy is materially below target — flag in the next owner package and assign a remediation owner.`,
    })
  }
  return recs
}
